import React, { useEffect, useMemo, useRef, useState } from 'react';
import { handleLeftOverflow, handlePercentageOffset, handlePositioning, handleRightOverflow } from './positionHandler';

import { useContextSelector } from '../../hooks';
import { Utils } from '../../utils';
import { Offset, OffsetProps, Positions } from './types';

interface ITooltipProps {
  text: string | React.ReactNode;
  children: React.ReactNode;
  containerClassName?: string;
  tooltipClassName?: string;
  position?: keyof typeof Positions;
  trigger?: boolean;
  offset?: OffsetProps;
}

const INITIAL_OFFSET: Offset = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

export const Tooltip = ({
  children,
  text,
  position = 'left',
  trigger = true,
  offset = INITIAL_OFFSET,
  containerClassName = '',
  tooltipClassName = '',
  ...props
}: ITooltipProps) => {
  const {
    generics: { isMobile },
  } = useContextSelector();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const OFFSET = useMemo(() => ({ ...INITIAL_OFFSET, ...offset }), [offset]);

  useEffect(() => {
    handleMouseEnter();
    handleMouseLeave();
    // eslint-disable-next-line
  }, []);

  useEffect(
    () =>
      Utils.helpers.handleClosePopUp(containerRef, () => {
        handleMouseLeave();
        setIsVisible(false);
      }),
    [],
  );

  const handleMouseEnter = () => {
    const container = containerRef.current as HTMLDivElement;
    const tooltip = tooltipRef.current as HTMLSpanElement;

    if (trigger) {
      tooltip.classList.add('visible');

      const containerRect = container.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      let width = tooltipRect?.width || 0;
      if (!isMobile && /^left/.test(position)) {
        width = handleLeftOverflow(tooltipRect, screenWidth);
        setScreenWidth(window.innerWidth);
      }

      if (!isMobile && /^right/.test(position)) {
        width = handleRightOverflow(tooltipRect, screenWidth);
        setScreenWidth(window.innerWidth);
      }

      const childrenContainer = container.firstElementChild as HTMLElement;
      const offsetObj: Offset = handlePercentageOffset(childrenContainer, containerRect, OFFSET);

      handlePositioning(position, containerRect, Object.assign(tooltipRect, { width }), tooltip, offsetObj);
    }
  };

  const handleMouseLeave = () => {
    const tooltip = tooltipRef.current as HTMLSpanElement;
    tooltip.classList.remove('visible');
  };

  const handleClick = () => {
    if (isMobile) {
      if (isVisible) {
        handleMouseLeave();
      } else {
        handleMouseEnter();
      }

      setIsVisible((prevState) => !prevState);
    }
  };

  return (
    <section
      ref={containerRef}
      className={`tooltip-container ${containerClassName}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      {...props}
    >
      {children}
      <span ref={tooltipRef} className={`tooltip-content ${tooltipClassName}`.trim()}>
        {text}
      </span>
    </section>
  );
};
